import type React from "react";
import { ActionIcon, Group, Tooltip } from "@mantine/core";
import { Table } from "@xplatform/design-system";
import {
  IconCheck,
  IconHistory,
  IconRotateClockwise,
  IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

interface TmRowActionsProps {
  hasTopMatch: boolean;
  actionsDisabled: boolean;
  isSaving: boolean;
  isRejected: boolean;
  onAccept: () => void;
  onReject: () => void;
  onRecover: () => void;
  onOpenHistory: () => void;
}

/** TM actions column: accept the (possibly edited) value, reject or recover,
 * and open revision history. No overflow menu or custom-correction modal. */
const TmRowActions: React.FC<TmRowActionsProps> = ({
  hasTopMatch,
  actionsDisabled,
  isSaving,
  isRejected,
  onAccept,
  onReject,
  onRecover,
  onOpenHistory,
}) => {
  const { t } = useTranslation();

  return (
    <Table.Td>
      <Group gap={4} wrap="nowrap" justify="flex-start">
        <Tooltip label={t("resultRow.actions.accept")}>
          <ActionIcon
            size="sm"
            variant="light"
            color="teal"
            onClick={onAccept}
            disabled={!hasTopMatch || actionsDisabled || isRejected}
            loading={isSaving}
            aria-label={t("resultRow.actions.accept")}
          >
            <IconCheck size={14} />
          </ActionIcon>
        </Tooltip>
        {isRejected ? (
          <Tooltip
            label={t("resultRow.actions.recover", { defaultValue: "Recover" })}
          >
            <ActionIcon
              size="sm"
              variant="light"
              color="blue"
              onClick={onRecover}
              disabled={actionsDisabled}
              loading={isSaving}
              aria-label={t("resultRow.actions.recover", {
                defaultValue: "Recover",
              })}
            >
              <IconRotateClockwise size={14} />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Tooltip label={t("resultRow.actions.reject")}>
            <ActionIcon
              size="sm"
              variant="light"
              color="red"
              onClick={onReject}
              disabled={actionsDisabled}
              loading={isSaving}
              aria-label={t("resultRow.actions.reject")}
            >
              <IconX size={14} />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label={t("resultRow.actions.history")}>
          <ActionIcon
            size="sm"
            variant="subtle"
            color="blue"
            onClick={onOpenHistory}
            aria-label={t("resultRow.actions.history")}
          >
            <IconHistory size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Table.Td>
  );
};

export default TmRowActions;
